import { InvoiceData, PrintSettings, DispatchPrintVariant } from '@/types/invoice';
import { TabItem } from '@/types';
import { PrinterTemplate, PrinterConfig, PaperWidthMm } from '../types';
import { lineCharsForPaperWidth, normalizePaperWidth } from '../paperWidth';
import { format } from 'date-fns';
import { COLONES_PAYMENT_METHOD, DOLLAR_PAYMENT_METHOD } from '@/constants/defaultPaymentMethodContant';
import { formatCurrency } from '@/lib/currency';
import { useOrganizationStore } from '@/stores/organizationStore';
import { calculateTabDiscountAmount } from '@/utils/tabTotals';
import { formatWeight, isWeightLine } from '@/utils/weightPricing';

// blank lines before the final cut
const TICKET_TRAILING_FEED_LINES = 3;

/** Short, playful kitchen-ticket footers. Wrapped to the printer's character width. */
const KITCHEN_DISPATCH_PHRASES = [
  'Recordá divertirte.',
  'Que fluya la cocina.',
  'A darle con sabor.',
  'Hoy se cocina con estilo.',
  'No te quemes... mucho.',
  'Pedidos con buena vibra.',
  'A cocinar como campeón.',
  '¡Hazlo rico, hazlo rico!',
  'Más fuego, menos drama.',
  'Cocina feliz, vive feliz.',
  'Que no se enfríe el ánimo.',
  'Plato listo, sonrisa lista',
  'El hambre no espera.',
  'Brilla como ese sartén.',
  'Servicio con chispa.',
  'A fuego lento... o no',
  'Hoy toca ser leyenda.',
  '¡Sazón al máximo!',
  'Que se note el talento.',
  'Cada plato cuenta una historia.',
  'Cada segundo cuenta.',
  '¡No te duermas, crack!',
  '¡Dale gas sin miedo!',
  '¡Que arda esa cocina!',
  'Sin drama, con sazón.',
  '¡A matar el hambre!',
  'Métale ganas.',
  'Arte en cada sartén.',
  'Chef mode: activado.',
  'Que hable el sabor.',
  'Ritmo, fuego y flow.',
  'Esto no es cocina: es show.',
  'Plato con actitud.',
  'Sazón, estilo y cero miedo.',
  'Hoy se cocina en grande.',
  '¿Alguien lee esto?',
  'No se distraiga leyendo esto.',
  'No leer esta línea.',
  'El que lee esta línea invita a las birras',
] as const;

const getRandomKitchenPhrase = (): string => {
  const index = Math.floor(Math.random() * KITCHEN_DISPATCH_PHRASES.length);
  return KITCHEN_DISPATCH_PHRASES[index];
};

const appendTrailingFeed = (commands: Array<{ method: string; params: unknown[] }>) => {
  commands.push({ method: 'addFeedLine', params: [TICKET_TRAILING_FEED_LINES] });
};

/**
 * Get default print settings for Epson thermal printer
 */
const getDefaultSettings = (): PrintSettings => {
  return {
    paperWidth: 80,
    fontSize: {
      header: 14,
      normal: 10,
      small: 8,
    },
    margins: {
      top: 5,
      bottom: 5,
      left: 5,
      right: 5,
    },
    includeQRCode: false,
    includeLogo: false,
    language: 'es',
    currency: 'USD',
  };
};

type ThermalCommand = { method: string; params: unknown[] };

const DEFAULT_AMOUNT_RIGHT_OFFSET = 4;
// TODO: remove Sibu Escaleras printer workaround once that device's right-edge clipping is fixed
const SIBU_ESCALERAS_SLUG = 'sibu-escaleras';
const SIBU_ESCALERAS_AMOUNT_RIGHT_OFFSET = 6;

const wrapThermalText = (text: string, maxLen: number): string[] => {
  if (text.length <= maxLen) {
    return [text];
  }

  const lines: string[] = [];
  for (let index = 0; index < text.length; index += maxLen) {
    lines.push(text.slice(index, index + maxLen));
  }
  return lines;
};

const padColumns = (leftText: string, rightText: string, width: number): string => {
  if (rightText.length >= width) {
    return rightText.slice(0, width);
  }
  const leftWidth = width - rightText.length;
  const left = leftText.length > leftWidth ? leftText.slice(0, leftWidth) : leftText.padEnd(leftWidth);
  return `${left}${rightText}`;
};

type ThermalLayout = {
  maxLineLength: number;
  amountColumnWidth: number;
  wrap: (text: string, maxLen?: number) => string[];
  wrapDoubleWidth: (text: string) => string[];
  padLine: (leftText: string, rightText: string) => string;
  padAmountLine: (leftText: string, rightText: string) => string;
  separator: () => string;
  dashLine: () => string;
  formatItem: (item: TabItem, currency: string) => string;
};

const createThermalLayout = (paperWidth?: number): ThermalLayout => {
  const maxLineLength = lineCharsForPaperWidth(normalizePaperWidth(paperWidth));
  const slug = useOrganizationStore.getState().currentOrganizationSlug;
  const offset =
    slug === SIBU_ESCALERAS_SLUG ? SIBU_ESCALERAS_AMOUNT_RIGHT_OFFSET : DEFAULT_AMOUNT_RIGHT_OFFSET;
  const amountColumnWidth = maxLineLength - offset;

  const wrap = (text: string, maxLen = maxLineLength) => wrapThermalText(text, maxLen);

  return {
    maxLineLength,
    amountColumnWidth,
    wrap,
    wrapDoubleWidth: (text: string) => wrap(text, Math.max(8, Math.floor(maxLineLength / 2))),
    padLine: (leftText, rightText) => padColumns(leftText, rightText, maxLineLength),
    padAmountLine: (leftText, rightText) => padColumns(leftText, rightText, amountColumnWidth),
    separator: () => `${'='.repeat(amountColumnWidth)}\n`,
    dashLine: () => `${'-'.repeat(amountColumnWidth)}\n`,
    formatItem: (item, currency) => {
      const itemTotal = item.unitPrice * item.quantity * (1 - item.discount / 100);
      const hasWeight = isWeightLine(item);
      const quantity = hasWeight
        ? `${formatWeight(item.weightKg!)} `.padEnd(3)
        : item.quantity.toString().padEnd(3);
      const total = formatPrice(itemTotal, currency);
      const nameWidth = Math.max(1, amountColumnWidth - quantity.length - total.length);
      const rawName = item.product.name;
      const productName =
        rawName.length > nameWidth
          ? `${rawName.slice(0, Math.max(1, nameWidth - 3))}...`
          : rawName.padEnd(nameWidth);
      return `${quantity}${productName}${total}`;
    },
  };
};

// Create a reusable header that can be used in multiple templates
const createHeader = (
  invoiceData: InvoiceData,
  isPaymentReceipt: boolean,
  layout: ThermalLayout,
): Array<{ method: string; params: unknown[] }> => {
  const commands: Array<{ method: string; params: unknown[] }> = [];

  commands.push({ method: 'addTextAlign', params: ['center'] });
  commands.push({ method: 'addTextFont', params: ['font_a'] });
  commands.push({ method: 'addTextSize', params: [2, 2] });
  layout.wrapDoubleWidth(invoiceData.organizationName.toUpperCase()).forEach((line) => {
    commands.push({ method: 'addText', params: [`${line}\n`] });
  });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  commands.push({ method: 'addTextFont', params: ['font_a'] });
  if (invoiceData.organizationSlogan) {
    layout.wrap(invoiceData.organizationSlogan).forEach((line) => {
      commands.push({ method: 'addText', params: [`${line}\n`] });
    });
  }
  if (invoiceData.organizationAddress) {
    layout.wrap(invoiceData.organizationAddress).forEach((line) => {
      commands.push({ method: 'addText', params: [`${line}\n`] });
    });
  }
  if (invoiceData.organizationPhone) commands.push({ method: 'addText', params: [`Teléfono: ${invoiceData.organizationPhone}\n`] });
  if (invoiceData.organizationLegalName) {
    layout.wrap(invoiceData.organizationLegalName).forEach((line) => {
      commands.push({ method: 'addText', params: [`${line}\n`] });
    });
  }
  if (invoiceData.organizationLegalId) commands.push({ method: 'addText', params: [`Cedula: ${invoiceData.organizationLegalId}\n`] });
  commands.push({ method: 'addFeedLine', params: [2] });
  //TODO: Translate
  if (!isPaymentReceipt) commands.push({ method: 'addText', params: ['Cuenta Pendiente \n'] });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  commands.push({ method: 'addTextAlign', params: ['left'] });
  commands.push({ method: 'addText', params: [`Fecha: ${format(invoiceData.createdAt, 'dd/MM/yyyy HH:mm')}\n`] });
  if (invoiceData.tableName) {
    commands.push({ method: 'addText', params: [`Mesa: ${invoiceData.tableName}\n`] });
  }
  commands.push({ method: 'addText', params: [`Cliente: ${invoiceData.customerName}\n`] });
  if (invoiceData.serverName) {
    commands.push({ method: 'addText', params: [`Salonero: ${invoiceData.serverName}\n`] });
  }
  const facturaReference = resolveFacturaReference(invoiceData);
  if (facturaReference) {
    commands.push({ method: 'addText', params: [`Factura: ${facturaReference}\n`] });
  }
  if (isPaymentReceipt && invoiceData.tabId) {
    commands.push({ method: 'addText', params: [`${formatOrderNumberLabel(invoiceData.tabId, 'Orden')}\n`] });
  }
  if (invoiceData.electronicInvoiceClave) {
    commands.push({ method: 'addText', params: ['Clave:\n'] });
    layout.wrap(invoiceData.electronicInvoiceClave).forEach((line) => {
      commands.push({ method: 'addText', params: [`${line}\n`] });
    });
  }

  return commands;
};

const formatPrice = (price: number, currency: string): string => {
  return formatCurrency(price, currency).replace(/₡/g, '¢');
};

const resolveFacturaReference = (invoiceData: InvoiceData): string => {
  const reference = invoiceData.id?.trim();
  if (reference) {
    return reference;
  }

  return invoiceData.tabId ? invoiceData.tabId.slice(-8) : '';
};

const formatOrderNumberLabel = (tabId: string, label: 'ORDEN' | 'Orden' = 'ORDEN'): string =>
  `${label}: #${tabId.slice(-3).toUpperCase()}`;

const appendReportHeader = (
  commands: ThermalCommand[],
  layout: ThermalLayout,
  {
    title,
    organizationName,
    detailLines,
  }: {
    title: string;
    organizationName: string;
    detailLines: string[];
  },
) => {
  commands.push({ method: 'addTextAlign', params: ['center'] });
  commands.push({ method: 'addTextFont', params: ['font_a'] });
  commands.push({ method: 'addTextSize', params: [2, 2] });
  layout.wrapDoubleWidth(title).forEach((line) => {
    commands.push({ method: 'addText', params: [`${line}\n`] });
  });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  layout.wrap(organizationName.toUpperCase()).forEach((line) => {
    commands.push({ method: 'addText', params: [`${line}\n`] });
  });
  commands.push({ method: 'addFeedLine', params: [1] });
  commands.push({ method: 'addTextAlign', params: ['left'] });
  detailLines.forEach((line) => {
    layout.wrap(line).forEach((wrapped) => {
      commands.push({ method: 'addText', params: [`${wrapped}\n`] });
    });
  });
  commands.push({ method: 'addText', params: [layout.separator()] });
};

const appendReportFooter = (commands: ThermalCommand[], layout: ThermalLayout) => {
  commands.push({ method: 'addText', params: [layout.separator()] });
  commands.push({ method: 'addFeedLine', params: [1] });
  commands.push({ method: 'addTextAlign', params: ['center'] });
  commands.push({ method: 'addTextFont', params: ['font_b'] });
  commands.push({ method: 'addText', params: ['Powered by Hermosa Software\n'] });
  appendTrailingFeed(commands);
};

interface DispatchOrderData {
  organizationName: string;
  tabId: string;
  customerName: string;
  tableName?: string;
  createdAt: Date;
  items: TabItem[];
  serverName?: string;
  showCategories?: boolean;
}

export interface CashBalancePrintData {
  date: string;
  organizationName: string;
  userName: string;
  expectedCash: number;
  actualCash: number;
  cashDifference: number;
  expectedCard: number;
  actualCard: number;
  cardDifference: number;
  notes?: string;
  withdrawals?: number;
  /** Float carried in from the previous balanced day. */
  openingCash?: number;
  /** Cash sales of the day, excluding the opening float. */
  cashSales?: number;
  /** Float left in the register for the next day. */
  startingCash?: number;
  dollarCashReceived?: number;
  exchangeRate?: number;
  /** Counted organization-currency cash, when dollars were counted separately. */
  actualCashLocal?: number | null;
  actualCashUsd?: number | null;
  expectedCashLocal?: number | null;
  expectedCashUsd?: number | null;
  changeOnDollarPayments?: number;
  /** Organization currency code at close. */
  currencyCode?: string | null;
  referencePayments?: Array<{ name: string; amount: number; excludedFromRevenue?: boolean }>;
  serviceChargeTotal?: number;
  ivaTotal?: number;
  serviceChargeRate?: number;
  ivaRate?: number;
  showExpectedCash?: boolean;
}

export interface CashWithdrawalPrintData {
  organizationName: string;
  userName: string;
  amount: number;
  justification: string;
  date: string;
  withdrawnAt: Date;
}

const formatDifference = (amount: number, currency: string): string => {
  const prefix = amount > 0 ? '+' : '';
  return `${prefix}${formatPrice(amount, currency)}`;
};

const appendPaymentMethodSection = (
  commands: Array<{ method: string; params: unknown[] }>,
  layout: ThermalLayout,
  label: string,
  expected: number,
  declared: number | null,
  difference: number | null,
  currency: string,
  referenceNote = 'Solo referencia',
  showExpected = true,
  extraLines: string[] = [],
) => {
  commands.push({ method: 'addText', params: [`${label}\n`] });
  if (showExpected) {
    commands.push({ method: 'addText', params: [`  Esperado: ${formatPrice(expected, currency)}\n`] });
  } else if (declared == null) {
    commands.push({ method: 'addText', params: [`  Monto: ${formatPrice(expected, currency)}\n`] });
  }
  if (declared != null) {
    commands.push({ method: 'addText', params: [`  Declarado: ${formatPrice(declared, currency)}\n`] });
  }
  if (showExpected) {
    if (difference != null) {
      commands.push({ method: 'addText', params: [`  Diferencia: ${formatDifference(difference, currency)}\n`] });
    } else {
      commands.push({ method: 'addText', params: [`  ${referenceNote}\n`] });
    }
  } else if (declared == null) {
    commands.push({ method: 'addText', params: [`  ${referenceNote}\n`] });
  }
  for (const line of extraLines) {
    commands.push({ method: 'addText', params: [`${line}\n`] });
  }
  commands.push({ method: 'addText', params: [layout.dashLine()] });
};

/**
 * Create cash balance closing template for Epson printer
 */
export const createCashBalanceTemplate = (
  data: CashBalancePrintData,
  config: PrinterConfig,
): PrinterTemplate => {
  const commands: ThermalCommand[] = [];
  const layout = createThermalLayout(config.paperWidth);
  const currency = config.currency || 'CRC';
  const balanceDate = new Date(`${data.date}T12:00:00`);
  const openingCash = data.openingCash ?? 0;
  const withdrawals = data.withdrawals ?? 0;
  const cashSales = data.cashSales ?? (data.expectedCash - openingCash + withdrawals);
  const cashSalesActual = cashSales + data.cashDifference;
  const totalExpected = data.expectedCash + data.expectedCard;
  const totalActual = data.actualCash + data.actualCard;
  const totalExpectedNoOpening = cashSales + data.expectedCard;
  const totalActualNoOpening = cashSalesActual + data.actualCard;
  const totalDifference = data.cashDifference + data.cardDifference;
  const showExpectedCash = data.showExpectedCash !== false;
  const hasDollarSplit =
    data.actualCashUsd != null &&
    data.actualCashLocal != null &&
    data.expectedCashUsd != null &&
    data.expectedCashUsd > 0;
  const localCode = data.currencyCode || currency;

  appendReportHeader(commands, layout, {
    title: 'CIERRE DE CAJA',
    organizationName: data.organizationName,
    detailLines: [
      `Fecha: ${format(balanceDate, 'dd/MM/yyyy')}`,
      `Realizado por: ${data.userName}`,
    ],
  });

  if (hasDollarSplit) {
    const localExpected = data.expectedCashLocal ?? data.expectedCash;
    const localActual = data.actualCashLocal ?? 0;
    const localDifference = localActual - localExpected;
    const dollarDifference = (data.actualCashUsd ?? 0) - (data.expectedCashUsd ?? 0);
    appendPaymentMethodSection(
      commands,
      layout,
      `Efectivo ${localCode}`,
      localExpected,
      localActual,
      localDifference,
      currency,
      undefined,
      showExpectedCash,
      (data.changeOnDollarPayments ?? 0) > 0
        ? [`  Vuelto en pagos USD: ${formatPrice(data.changeOnDollarPayments ?? 0, currency)}`]
        : [],
    );
    appendPaymentMethodSection(
      commands,
      layout,
      'Dolares',
      data.expectedCashUsd ?? 0,
      data.actualCashUsd ?? 0,
      dollarDifference,
      'USD',
      undefined,
      showExpectedCash,
    );
  } else {
    appendPaymentMethodSection(
      commands,
      layout,
      'Efectivo',
      data.expectedCash,
      data.actualCash,
      data.cashDifference,
      currency,
      undefined,
      showExpectedCash,
      openingCash > 0
        ? [
            '  Sin caja inicial:',
            ...(showExpectedCash ? [`    Esperado: ${formatPrice(cashSales, currency)}`] : []),
            `    Declarado: ${formatPrice(cashSalesActual, currency)}`,
          ]
        : [],
    );
  }
  appendPaymentMethodSection(
    commands,
    layout,
    'Tarjeta',
    data.expectedCard,
    data.actualCard,
    data.cardDifference,
    currency,
    undefined,
    showExpectedCash,
  );

  for (const payment of data.referencePayments ?? []) {
    if (!payment?.amount || payment.amount <= 0) continue;
    appendPaymentMethodSection(
      commands,
      layout,
      payment.name,
      payment.amount,
      null,
      null,
      currency,
      payment.excludedFromRevenue
        ? 'Solo referencia (no cuenta en ventas)'
        : 'Solo referencia',
      showExpectedCash,
    );
  }

  commands.push({ method: 'addTextSize', params: [1, 2] });
  commands.push({ method: 'addText', params: ['TOTAL\n'] });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  if (showExpectedCash) {
    commands.push({ method: 'addText', params: [`  Esperado: ${formatPrice(totalExpected, currency)}\n`] });
  }
  commands.push({ method: 'addText', params: [`  Declarado: ${formatPrice(totalActual, currency)}\n`] });
  if (showExpectedCash) {
    commands.push({ method: 'addText', params: [`  Diferencia: ${formatDifference(totalDifference, currency)}\n`] });
  }
  if (openingCash > 0) {
    commands.push({ method: 'addText', params: ['  Sin caja inicial:\n'] });
    if (showExpectedCash) {
      commands.push({ method: 'addText', params: [`    Esperado: ${formatPrice(totalExpectedNoOpening, currency)}\n`] });
    }
    commands.push({ method: 'addText', params: [`    Declarado: ${formatPrice(totalActualNoOpening, currency)}\n`] });
  }
  commands.push({ method: 'addText', params: [layout.dashLine()] });

  if ((data.serviceChargeTotal ?? 0) > 0 || (data.ivaTotal ?? 0) > 0) {
    commands.push({ method: 'addText', params: ['RESUMEN DE IMPUESTOS\n'] });
    if ((data.serviceChargeTotal ?? 0) > 0) {
      const serviceLabel = data.serviceChargeRate
        ? `Servicio ${data.serviceChargeRate}%`
        : 'Servicio';
      commands.push({
        method: 'addText',
        params: [layout.padLine(serviceLabel, formatPrice(data.serviceChargeTotal!, currency)) + '\n'],
      });
    }
    if ((data.ivaTotal ?? 0) > 0) {
      const ivaLabel = data.ivaRate ? `IVA ${data.ivaRate}%` : 'IVA';
      commands.push({
        method: 'addText',
        params: [layout.padLine(ivaLabel, formatPrice(data.ivaTotal!, currency)) + '\n'],
      });
    }
    commands.push({ method: 'addText', params: [layout.dashLine()] });
  }

  if (data.openingCash && data.openingCash > 0) {
    commands.push({ method: 'addText', params: [layout.padLine('Caja inicial', formatPrice(data.openingCash, currency)) + '\n'] });
  }

  if (data.withdrawals && data.withdrawals > 0) {
    commands.push({ method: 'addText', params: [layout.padLine('Retiros realizados', formatPrice(data.withdrawals, currency)) + '\n'] });
  }

  if (hasDollarSplit) {
    const leftInRegister = data.startingCash ?? 0;
    if (leftInRegister > 0) {
      commands.push({ method: 'addText', params: [layout.padLine('Se deja en caja', formatPrice(leftInRegister, currency)) + '\n'] });
    }
    const localNet = (data.actualCashLocal ?? 0) - leftInRegister;
    if (localNet >= 0) {
      commands.push({
        method: 'addText',
        params: [layout.padLine(`${localCode} a depositar`, formatPrice(localNet, currency)) + '\n'],
      });
    } else {
      commands.push({
        method: 'addText',
        params: [layout.padLine('Aporte a caja', formatPrice(-localNet, currency)) + '\n'],
      });
    }
    commands.push({
      method: 'addText',
      params: [layout.padLine('Dolares a depositar', formatPrice(data.actualCashUsd ?? 0, 'USD')) + '\n'],
    });
    if (data.exchangeRate) {
      commands.push({
        method: 'addText',
        params: [layout.padLine('Tipo de cambio', data.exchangeRate.toFixed(2)) + '\n'],
      });
    }
  } else if (data.startingCash != null && data.startingCash > 0) {
    commands.push({ method: 'addText', params: [layout.padLine('Se deja en caja', formatPrice(data.startingCash, currency)) + '\n'] });
    const netToDeposit = data.actualCash - data.startingCash;
    if (netToDeposit >= 0) {
      commands.push({
        method: 'addText',
        params: [layout.padLine('A depositar', formatPrice(netToDeposit, currency)) + '\n'],
      });
    } else {
      commands.push({
        method: 'addText',
        params: [layout.padLine('Aporte a caja', formatPrice(-netToDeposit, currency)) + '\n'],
      });
    }
  }

  if (!hasDollarSplit && data.dollarCashReceived && data.dollarCashReceived > 0) {
    commands.push({
      method: 'addText',
      params: [layout.padLine('Dolares en efectivo', `$${data.dollarCashReceived.toFixed(2)}`) + '\n'],
    });
    if (data.exchangeRate) {
      commands.push({
        method: 'addText',
        params: [layout.padLine('Tipo de cambio', data.exchangeRate.toFixed(2)) + '\n'],
      });
    }
  }

  if (data.notes?.trim()) {
    commands.push({ method: 'addText', params: [`Notas: ${data.notes.trim()}\n`] });
  }

  appendReportFooter(commands, layout);

  return {
    commands,
    metadata: {
      type: 'cash_balance',
      timestamp: new Date(),
    },
  };
};

/**
 * Create cash withdrawal template for Epson printer
 */
export const createCashWithdrawalTemplate = (
  data: CashWithdrawalPrintData,
  config: PrinterConfig,
): PrinterTemplate => {
  const commands: ThermalCommand[] = [];
  const layout = createThermalLayout(config.paperWidth);
  const currency = config.currency || 'CRC';
  const withdrawalDate = new Date(`${data.date}T12:00:00`);

  appendReportHeader(commands, layout, {
    title: 'RETIRO DE CAJA',
    organizationName: data.organizationName,
    detailLines: [
      `Fecha: ${format(withdrawalDate, 'dd/MM/yyyy')}`,
      `Hora: ${format(data.withdrawnAt, 'HH:mm')}`,
      `Retirado por: ${data.userName}`,
    ],
  });

  commands.push({ method: 'addTextSize', params: [1, 2] });
  commands.push({
    method: 'addText',
    params: [`${layout.padLine('Monto', formatPrice(data.amount, currency))}\n`],
  });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  commands.push({ method: 'addText', params: [layout.dashLine()] });
  commands.push({ method: 'addText', params: [`Justificacion: ${data.justification.trim()}\n`] });

  appendReportFooter(commands, layout);

  return {
    commands,
    metadata: {
      type: 'cash_withdrawal',
      timestamp: new Date(),
    },
  };
};

const appendWrappedLines = (commands: ThermalCommand[], layout: ThermalLayout, text: string) => {
  layout.wrap(text).forEach((line) => {
    commands.push({ method: 'addText', params: [`${line}\n`] });
  });
};

const appendDispatchItems = (
  commands: ThermalCommand[],
  items: TabItem[],
  layout: ThermalLayout,
  showCategories = false,
) => {
  items.forEach((item) => {
    const itemName = item.product.name || 'Producto';
    const itemNotes = item.notes?.trim();
    const hasWeight = isWeightLine(item);
    const qtyLabel = hasWeight
      ? formatWeight(item.weightKg!)
      : `${item.quantity} x`;

    commands.push({ method: 'addTextSize', params: [2, 2] });
    layout.wrapDoubleWidth(`${qtyLabel} ${itemName}`).forEach((line) => {
      commands.push({ method: 'addText', params: [`${line}\n`] });
    });
    commands.push({ method: 'addTextSize', params: [1, 1] });

    const categoryName = showCategories ? item.product.productCategory?.name?.trim() : undefined;
    if (categoryName) {
      appendWrappedLines(commands, layout, `   Categoria: ${categoryName}`);
    }

    const modifiers = item.modifiers ?? [];
    if (modifiers.length > 0) {
      commands.push({ method: 'addTextSize', params: [1, 2] });
      for (const modifier of modifiers) {
        appendWrappedLines(commands, layout, `   + ${modifier.name}`);
      }
      commands.push({ method: 'addTextSize', params: [1, 1] });
    }

    if (item.promotionComponents?.length) {
      for (const comp of item.promotionComponents) {
        const compQty = comp.quantity * item.quantity;
        appendWrappedLines(commands, layout, `   - ${comp.productName} x${compQty}`);
      }
    }

    if (itemNotes) {
      commands.push({ method: 'addTextSize', params: [1, 2] });
      appendWrappedLines(commands, layout, `   Nota: ${itemNotes}`);
      commands.push({ method: 'addTextSize', params: [1, 1] });
    }
    if (item.customerPosition) {
      appendWrappedLines(commands, layout, `   Puesto: ${item.customerPosition}`);
    }
    commands.push({ method: 'addFeedLine', params: [1] });
  });
};

const appendDispatchTicketHeader = (
  commands: ThermalCommand[],
  headerLabel: string,
  dispatchData: Omit<DispatchOrderData, 'items'>,
  layout: ThermalLayout,
) => {
  commands.push({ method: 'addTextAlign', params: ['center'] });
  commands.push({ method: 'addTextFont', params: ['font_a'] });
  commands.push({ method: 'addTextSize', params: [2, 2] });
  layout.wrapDoubleWidth(headerLabel).forEach((line) => {
    commands.push({ method: 'addText', params: [`${line}\n`] });
  });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  commands.push({ method: 'addTextFont', params: ['font_a'] });
  appendWrappedLines(commands, layout, dispatchData.organizationName.toUpperCase());
  commands.push({ method: 'addFeedLine', params: [1] });

  commands.push({ method: 'addTextSize', params: [2, 2] });
  layout.wrapDoubleWidth(formatOrderNumberLabel(dispatchData.tabId)).forEach((line) => {
    commands.push({ method: 'addText', params: [`${line}\n`] });
  });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  commands.push({ method: 'addFeedLine', params: [1] });

  commands.push({ method: 'addTextAlign', params: ['left'] });
  if (dispatchData.tableName) {
    appendWrappedLines(commands, layout, `Mesa: ${dispatchData.tableName}`);
  }
  appendWrappedLines(commands, layout, `Cliente: ${dispatchData.customerName}`);
  if (dispatchData.serverName) {
    appendWrappedLines(commands, layout, `Salonero: ${dispatchData.serverName}`);
  }
  appendWrappedLines(commands, layout, `Fecha: ${format(dispatchData.createdAt, 'dd/MM/yyyy HH:mm')}`);
  commands.push({ method: 'addText', params: [layout.dashLine()] });
};

/**
 * Create dispatch order template for Epson printer (kitchen ticket)
 */
export const createDispatchOrderTemplate = (
  dispatchData: DispatchOrderData,
  area: 'cocina' | 'bar',
  variant: DispatchPrintVariant = 'order',
  paperWidth?: PaperWidthMm,
): PrinterTemplate => {
  const commands: ThermalCommand[] = [];
  const layout = createThermalLayout(paperWidth);
  const headerLabel = variant === 'cancellation'
    ? `CANCELACION DE ${area.toUpperCase()}`
    : `ORDEN DE ${area.toUpperCase()}`;

  appendDispatchTicketHeader(commands, headerLabel, dispatchData, layout);
  appendDispatchItems(commands, dispatchData.items, layout, dispatchData.showCategories);

  commands.push({ method: 'addFeedLine', params: [2] });
  commands.push({ method: 'addTextAlign', params: ['center'] });
  commands.push({ method: 'addTextFont', params: ['font_b'] });
  layout.wrap(getRandomKitchenPhrase()).forEach((line) => {
    commands.push({ method: 'addText', params: [`${line}\n`] });
  });
  appendTrailingFeed(commands);

  return {
    commands,
    metadata: {
      type: variant === 'cancellation' ? 'dispatch_cancellation' : 'dispatch_order',
      timestamp: new Date(),
    },
  };
};

/**
 * One cashier ticket that copies kitchen and bar dispatch items together.
 */
export const createCashierDispatchCopyTemplate = (
  dispatchData: Omit<DispatchOrderData, 'items'>,
  sections: { kitchen: TabItem[]; bar: TabItem[] },
  variant: DispatchPrintVariant = 'order',
  paperWidth?: PaperWidthMm,
): PrinterTemplate => {
  const commands: ThermalCommand[] = [];
  const layout = createThermalLayout(paperWidth);
  const headerLabel = variant === 'cancellation' ? 'COPIA DE CANCELACION' : 'COPIA DE COMANDA';

  appendDispatchTicketHeader(commands, headerLabel, dispatchData, layout);

  if (sections.kitchen.length > 0) {
    commands.push({ method: 'addTextAlign', params: ['center'] });
    commands.push({ method: 'addTextSize', params: [2, 1] });
    commands.push({ method: 'addText', params: ['COCINA\n'] });
    commands.push({ method: 'addTextSize', params: [1, 1] });
    commands.push({ method: 'addTextAlign', params: ['left'] });
    appendDispatchItems(commands, sections.kitchen, layout, dispatchData.showCategories);
  }

  if (sections.bar.length > 0) {
    commands.push({ method: 'addTextAlign', params: ['center'] });
    commands.push({ method: 'addTextSize', params: [2, 1] });
    commands.push({ method: 'addText', params: ['BAR\n'] });
    commands.push({ method: 'addTextSize', params: [1, 1] });
    commands.push({ method: 'addTextAlign', params: ['left'] });
    appendDispatchItems(commands, sections.bar, layout, dispatchData.showCategories);
  }

  commands.push({ method: 'addFeedLine', params: [2] });
  commands.push({ method: 'addTextAlign', params: ['center'] });
  commands.push({ method: 'addTextFont', params: ['font_b'] });
  layout.wrap(getRandomKitchenPhrase()).forEach((line) => {
    commands.push({ method: 'addText', params: [`${line}\n`] });
  });
  appendTrailingFeed(commands);

  return {
    commands,
    metadata: {
      type: variant === 'cancellation' ? 'dispatch_cancellation' : 'dispatch_order',
      timestamp: new Date(),
    },
  };
};

/**
 * Create tab invoice template for Epson printer
 */
export const createTabInvoiceTemplate = (
  invoiceData: InvoiceData,
  config: PrinterConfig,
): PrinterTemplate => {

  const finalSettings = { ...getDefaultSettings(), ...config };
  const layout = createThermalLayout(finalSettings.paperWidth);
  const commands: Array<{ method: string; params: unknown[] }> = [];
  const isPaymentReceipt = !invoiceData.forPreview;

  // Header
  const header = createHeader(invoiceData, isPaymentReceipt, layout);
  const combinedCommands = commands.concat(header);
  // Body
  combinedCommands.push({ method: 'addText', params: [layout.separator()] });
  // Items
  for (const item of invoiceData.items) {
    const itemLine = layout.formatItem(item, finalSettings.currency);
    combinedCommands.push({ method: 'addText', params: [`${itemLine}\n`] });

    for (const modifier of item.modifiers ?? []) {
      combinedCommands.push({ method: 'addText', params: [`  + ${modifier.name}\n`] });
    }

    if (item.discount > 0) {
      combinedCommands.push({ method: 'addText', params: [`  Desc. ${item.discount}% \n`] });
    }
  }
  // Footer
  combinedCommands.push({ method: 'addText', params: [layout.dashLine()] });
  combinedCommands.push({ method: 'addTextAlign', params: ['left'] });
  combinedCommands.push({ method: 'addText', params: [`${layout.padAmountLine('Subtotal', formatPrice(invoiceData.subtotal, finalSettings.currency))}\n`] });
  const tabDiscountAmount = calculateTabDiscountAmount({
    discount: invoiceData.discount,
    items: invoiceData.items,
  });
  if (tabDiscountAmount > 0) {
    combinedCommands.push({
      method: 'addText',
      params: [`${layout.padAmountLine(`Descuento ${invoiceData.discount}%`, formatPrice(tabDiscountAmount, finalSettings.currency))}\n`],
    });
  }
  // Add Service Charge if enabled
  if (invoiceData.serviceCharge && invoiceData.serviceCharge > 0) {
    combinedCommands.push({ method: 'addText', params: [`${layout.padAmountLine(`Servicio ${invoiceData.serviceChargeRate}%`, formatPrice(invoiceData.serviceCharge, finalSettings.currency))}\n`] });
  }
  // Add IVA if enabled
  if (invoiceData.ivaCharge && invoiceData.ivaCharge > 0) {
    combinedCommands.push({ method: 'addText', params: [`${layout.padAmountLine(`IVA ${invoiceData.ivaRate}%`, formatPrice(invoiceData.ivaCharge, finalSettings.currency))}\n`] });
  }
  // Add Tip/Propina if present
  const tip = invoiceData.tip ?? 0;
  if (tip > 0) {
    combinedCommands.push({
      method: 'addText',
      params: [`${layout.padAmountLine('Propina', formatPrice(tip, finalSettings.currency))}\n`],
    });
  }
  combinedCommands.push({ method: 'addTextSize', params: [1, 2] });
  combinedCommands.push({ method: 'addText', params: [layout.padAmountLine('Total', formatPrice(invoiceData.total, finalSettings.currency)) + '\n'] });
  combinedCommands.push({ method: 'addTextSize', params: [1, 1] });
  if (invoiceData.totalInDollars && invoiceData.totalInDollars > 0) {
    combinedCommands.push({
      method: 'addText',
      params: [layout.padAmountLine('Total USD', formatPrice(invoiceData.totalInDollars, 'USD')) + '\n'],
    });
  }
  combinedCommands.push({ method: 'addText', params: [layout.dashLine()] });

  if (invoiceData.paymentMethod?.name) {
    combinedCommands.push({ method: 'addText', params: [layout.padAmountLine('Método de pago', invoiceData.paymentMethod.name) + '\n'] });
  }
  if (invoiceData.cashPaymentDetails) {
    const { amountReceived, changeGiven } = invoiceData.cashPaymentDetails;
    combinedCommands.push({ method: 'addText', params: [layout.padAmountLine('Recibido', formatPrice(amountReceived, finalSettings.currency)) + '\n'] });
    combinedCommands.push({ method: 'addText', params: [layout.padAmountLine('Cambio', formatPrice(changeGiven, finalSettings.currency)) + '\n'] });
  }
  combinedCommands.push({ method: 'addText', params: [layout.separator()] });
  combinedCommands.push({ method: 'addFeedLine', params: [1] });
  combinedCommands.push({ method: 'addTextAlign', params: ['center'] });
  combinedCommands.push({ method: 'addText', params: ['Gracias por su visita!'] });
  combinedCommands.push({ method: 'addFeedLine', params: [2] });
  combinedCommands.push({ method: 'addTextFont', params: ['font_b'] });
  combinedCommands.push({ method: 'addText', params: ['Powered by Hermosa Software'] });
  appendTrailingFeed(combinedCommands);
  const cashPaymentMethods = [COLONES_PAYMENT_METHOD, DOLLAR_PAYMENT_METHOD];
  const isCashPaymentMethod = cashPaymentMethods.includes(invoiceData.paymentMethod?.defaultPaymentMethodId as number);
  if (isPaymentReceipt && isCashPaymentMethod) combinedCommands.push({ method: 'addPulse', params: [] });

  return {
    commands: combinedCommands,
    metadata: {
      type: 'tab_check', // check this
      timestamp: new Date(),
    },
  };
};

/**
 * Create test page template for Epson printer
 */
export const createTestPageTemplate = (): PrinterTemplate => {

  const commands: Array<{ method: string; params: unknown[] }> = [];

  commands.push({ method: 'addTextAlign', params: ['center'] });
  commands.push({ method: 'addTextSize', params: [2, 2] });
  commands.push({ method: 'addText', params: ['TEST PAGE'] });
  commands.push({ method: 'addFeedLine', params: [1] });
  commands.push({ method: 'addTextSize', params: [1, 1] });
  commands.push({ method: 'addText', params: ['Epson Printer Test'] });
  commands.push({ method: 'addFeedLine', params: [1] });
  commands.push({ method: 'addText', params: [`Date: ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}`] });
  commands.push({ method: 'addFeedLine', params: [1] });
  commands.push({ method: 'addText', params: ['If you can see this, the printer is working!'] });
  appendTrailingFeed(commands);

  return {
    commands,
    metadata: {
      type: 'test_page',
      timestamp: new Date(),
    },
  };
};

// dispatchOrderTemplate here